import React from 'react';

// Base skeleton component with subtle animation
const SkeletonBase: React.FC<{ className?: string; children?: React.ReactNode }> = ({ 
  className = "", 
  children 
}) => (
  <div className={`skeleton-subtle ${className}`}>
    {children}
  </div>
);

// Text skeleton for various text elements
export const TextSkeleton: React.FC<{
  lines?: number;
  width?: 'full' | '3/4' | '2/3' | '1/2' | '1/3' | '1/4';
  size?: 'sm' | 'base' | 'lg' | 'xl';
  className?: string;
}> = ({ 
  lines = 1, 
  width = "full", 
  size = "base",
  className = ""
}) => {
  const widthClass = {
    'full': 'w-full',
    '3/4': 'w-3/4',
    '2/3': 'w-2/3',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '1/4': 'w-1/4'
  }[width];

  const heightClass = {
    'sm': 'h-3',
    'base': 'h-4',
    'lg': 'h-5',
    'xl': 'h-6'
  }[size];

  return (
    <SkeletonBase className={`space-y-2 ${className}`}>
      {Array(lines).fill(0).map((_, i) => (
        <div 
          key={i}
          className={`bg-zinc-800 rounded ${heightClass} ${
            i === lines - 1 && lines > 1 ? widthClass : 'w-full'
          }`} 
        />
      ))}
    </SkeletonBase>
  );
};

// Chart skeleton for line/bar charts
export const ChartSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <SkeletonBase className={className}>
    <div className="space-y-4">
      {/* Chart area */}
      <div className="h-64 bg-zinc-800 rounded-lg relative overflow-hidden">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent shimmer-effect" />
        
        {/* Mock chart elements */}
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          {[40, 60, 35, 80, 55, 70, 45].map((height, i) => (
            <div 
              key={i}
              className="bg-zinc-700/50 rounded-t"
              style={{ height: `${height}%`, width: '12%' }}
            />
          ))}
        </div>
      </div>
      
      {/* Legend skeleton */}
      <div className="flex justify-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-zinc-800 rounded-full"></div>
          <TextSkeleton width="1/4" size="sm" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-zinc-800 rounded-full"></div>
          <TextSkeleton width="1/4" size="sm" />
        </div>
      </div>
    </div>
  </SkeletonBase>
);

// Pie chart skeleton
export const PieChartSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <SkeletonBase className={className}>
    <div className="space-y-4">
      {/* Pie chart circle */}
      <div className="flex justify-center">
        <div className="w-48 h-48 bg-zinc-800 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent shimmer-effect" />
        </div>
      </div>
      
      {/* Legend */}
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-3 h-3 bg-zinc-800 rounded-full"></div>
            <TextSkeleton width="1/3" size="sm" />
            <TextSkeleton width="1/4" size="sm" />
          </div>
        ))}
      </div>
    </div>
  </SkeletonBase>
);

// Stats card skeleton
export const StatsSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <SkeletonBase className={`space-y-3 ${className}`}>
    <TextSkeleton size="sm" width="3/4" />
    <TextSkeleton size="xl" width="1/2" />
    <TextSkeleton size="sm" width="2/3" />
  </SkeletonBase>
);

// Progress bar skeleton
export const ProgressSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <SkeletonBase className={`space-y-3 ${className}`}>
    <div className="flex justify-between">
      <TextSkeleton size="sm" width="1/3" />
      <TextSkeleton size="sm" width="1/4" />
    </div>
    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full w-2/3 bg-zinc-700 rounded-full relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-600/20 to-transparent shimmer-effect" />
      </div>
    </div>
    <TextSkeleton size="sm" width="1/2" />
  </SkeletonBase>
);

// Card skeleton for endpoint cards
export const CardSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <SkeletonBase className={`bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 ${className}`}>
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <TextSkeleton size="lg" width="3/4" />
        <div className="w-6 h-6 bg-zinc-800 rounded"></div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <StatsSkeleton />
        <StatsSkeleton />
      </div>
      
      {/* Mini chart */}
      <div className="h-16 bg-zinc-800 rounded relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent shimmer-effect" />
      </div>
      
      {/* Footer */}
      <TextSkeleton size="sm" width="1/2" />
    </div>
  </SkeletonBase>
);

// Main dashboard skeleton layout
export const DashboardSkeleton: React.FC = () => (
  <div className="flex-1 px-4 py-4 overflow-y-auto">
    <div className="w-full pb-6">
      {/* Main Graph and Budget Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        {/* Main Usage/Cost Graph */}
        <div className="lg:col-span-3 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <TextSkeleton size="lg" width="1/2" />
            <div className="flex bg-zinc-800 rounded-lg p-1 gap-1">
              <div className="px-3 py-1.5 bg-zinc-700 rounded text-sm w-16 h-8"></div>
              <div className="px-3 py-1.5 rounded text-sm w-16 h-8 bg-zinc-800"></div>
            </div>
          </div>
          
          <ChartSkeleton />
          
          {/* Total Stats */}
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <div className="grid grid-cols-2 gap-6">
              <StatsSkeleton />
              <StatsSkeleton />
            </div>
          </div>
        </div>
        
        {/* Usage Panel */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <TextSkeleton size="lg" width="3/4" className="mb-6" />
          <ProgressSkeleton />
          <div className="mt-8 space-y-4">
            <StatsSkeleton />
            <StatsSkeleton />
            <StatsSkeleton />
          </div>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <TextSkeleton size="lg" width="1/3" />
          <div className="flex gap-2">
            <div className="w-8 h-8 bg-zinc-800 rounded"></div>
            <div className="w-8 h-8 bg-zinc-800 rounded"></div>
          </div>
        </div>
        
        {/* Search bar skeleton */}
        <div className="relative mb-4">
          <div className="w-full h-10 bg-zinc-800 rounded-xl"></div>
        </div>
        
        {/* Endpoints Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
      
      {/* Monthly Breakdown */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <TextSkeleton size="lg" width="1/2" className="mb-6" />
        <PieChartSkeleton />
      </div>
    </div>
  </div>
);

// Add subtle skeleton animations to global styles
const skeletonKeyframes = `
  @keyframes skeleton-subtle {
    0% { opacity: 0.8; }
    50% { opacity: 0.4; }
    100% { opacity: 0.8; }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  .skeleton-subtle {
    animation: skeleton-subtle 2.5s ease-in-out infinite;
  }
  
  .shimmer-effect {
    animation: shimmer 2s ease-in-out infinite;
  }
`;

// Component to inject styles
export const SkeletonStyles: React.FC = () => (
  <style dangerouslySetInnerHTML={{ __html: skeletonKeyframes }} />
);